import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CONTACT_KIND_LABELS, type Contact, type ContactSuggestion, type Inquiry } from '@kontor/shared';
import { api, ApiError, queryString } from '../../api';
import { useSession } from '../../session';
import './contacts.css';

const MATCH_LABELS = { email: 'gleiche E-Mail-Adresse', phone: 'gleiche Telefonnummer' } as const;

/**
 * Bereich in der Anfrage: zeigt den zugeordneten Kontakt oder schlägt vorhandene Kontakte vor. Es geschieht nichts von selbst,
 * das Büro ordnet zu oder legt aus den Angaben der Anfrage einen neuen Kontakt an. Ohne das Modul Kontakte oder ohne das Recht
 * zum Lesen erscheint der Bereich nicht.
 */
export function InquiryContactPanel({ inquiry }: { inquiry: Inquiry }) {
  const { can, hasModule } = useSession();
  if (!hasModule('contacts') || !can('contacts.read')) return null;
  return <Panel inquiry={inquiry} />;
}

/**
 * Kleine Schaltfläche „+ Kontakt anlegen“ für den Kopf der Karte „Kontakt“ in der Anfrage. Sie legt aus den Angaben der Anfrage einen Kontakt an und ordnet ihn zu.
 * Sie erscheint nur, wenn noch kein Kontakt zugeordnet ist und die Rolle Kontakte anlegen und Anfragen bearbeiten darf.
 */
export function InquiryContactCreateButton({ inquiry }: { inquiry: Inquiry }) {
  const { can, hasModule } = useSession();
  const client = useQueryClient();
  const allowed = hasModule('contacts') && can('inquiries.write') && can('contacts.write') && !inquiry.contactId;
  const create = useMutation({
    mutationFn: async () => {
      // Aus den Angaben der Anfrage. Eine Firma, die der Absender nennt, steht in der Notiz.
      const created = await api<{ data: Contact }>('POST', '/contacts', {
        kind: 'person',
        name: inquiry.contact.name,
        email: inquiry.contact.email,
        phone: inquiry.contact.phone ?? '',
        note: inquiry.contact.company ? `Firma laut Anfrage ${inquiry.reference}: ${inquiry.contact.company}` : '',
      });
      client.setQueryData(['contact', created.data.id], created.data);
      return api<{ data: Inquiry }>('PATCH', `/inquiries/${inquiry.id}`, { contactId: created.data.id });
    },
    onSuccess: (res) => {
      client.setQueryData(['inquiry', inquiry.id], res.data);
      client.invalidateQueries({ queryKey: ['inquiries'] });
      client.invalidateQueries({ queryKey: ['contacts'] });
      client.invalidateQueries({ queryKey: ['contact-inquiries'] });
    },
  });
  if (!allowed) return null;
  return (
    <>
      <button type="button" className="btn btn--primary btn--sm" disabled={create.isPending} onClick={() => create.mutate()} title="Legt aus den Angaben dieser Anfrage einen neuen Kontakt an und ordnet ihn zu">
        {create.isPending ? 'Wird angelegt …' : '+ Kontakt anlegen'}
      </button>
      {create.error instanceof ApiError && <span className="field__error" role="alert">{create.error.message}</span>}
    </>
  );
}

function Panel({ inquiry }: { inquiry: Inquiry }) {
  const { can } = useSession();
  const client = useQueryClient();
  const canAssign = can('inquiries.write');

  const linked = useQuery({
    queryKey: ['contact', inquiry.contactId],
    queryFn: async () => (await api<{ data: Contact }>('GET', `/contacts/${inquiry.contactId}`)).data,
    enabled: !!inquiry.contactId,
    retry: false,
  });
  const suggestions = useQuery({
    queryKey: ['contact-suggestions', inquiry.contact.email, inquiry.contact.phone],
    queryFn: async () => (await api<{ data: ContactSuggestion[] }>('GET', `/contacts/suggestions${queryString({ email: inquiry.contact.email, phone: inquiry.contact.phone ?? undefined })}`)).data,
    enabled: !inquiry.contactId,
  });

  const assign = useMutation({
    mutationFn: (contactId: string | null) => api<{ data: Inquiry }>('PATCH', `/inquiries/${inquiry.id}`, { contactId }),
    onSuccess: (res) => {
      client.setQueryData(['inquiry', inquiry.id], res.data);
      client.invalidateQueries({ queryKey: ['inquiries'] });
      client.invalidateQueries({ queryKey: ['contact-inquiries'] });
    },
  });

  const error = assign.error instanceof ApiError ? assign.error : undefined;
  const busy = assign.isPending;

  return (
    <div className="contact-panel">
      <h2>Kontakt im Kontaktbuch</h2>
      {error && <div className="alert alert--error" role="alert">{error.message}</div>}

      {inquiry.contactId && (
        <>
          {linked.isLoading && <p className="muted">Wird geladen …</p>}
          {linked.isError && <p className="muted">Der zugeordnete Kontakt ist nicht mehr vorhanden.</p>}
          {linked.data && (
            <p>
              <Link to={`/kontakte/${linked.data.id}`} className="strong-link">{linked.data.name}</Link>{' '}
              <span className="badge">{CONTACT_KIND_LABELS[linked.data.kind]}</span>
              <br />
              <span className="muted small">{[linked.data.email, linked.data.phone].filter(Boolean).join(' · ') || 'Keine Kontaktdaten'}</span>
            </p>
          )}
          {canAssign && (
            <button type="button" className="btn btn--secondary btn--sm" disabled={busy} onClick={() => assign.mutate(null)}>
              Zuordnung lösen
            </button>
          )}
        </>
      )}

      {!inquiry.contactId && (
        <>
          {suggestions.isLoading && <p className="muted">Suche nach passenden Kontakten …</p>}
          {suggestions.data && suggestions.data.length > 0 && (
            <>
              <p className="muted small">Diese Kontakte passen zu den Angaben in der Anfrage:</p>
              <ul className="contact-options">
                {suggestions.data.map((c) => (
                  <li key={c.id}>
                    <span className="contact-options__who">
                      <Link to={`/kontakte/${c.id}`} className="strong-link">{c.name}</Link>
                      <br />
                      <span className="muted small">{c.matchedBy.map((m) => MATCH_LABELS[m]).join(' und ')}</span>
                    </span>
                    {canAssign && (
                      <button type="button" className="btn btn--secondary btn--sm" disabled={busy} onClick={() => assign.mutate(c.id)}>
                        Zuordnen
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
          {suggestions.data?.length === 0 && <p className="muted small">Noch kein Kontakt mit dieser E-Mail-Adresse oder Telefonnummer. Oben rechts legen Sie ihn mit „+ Kontakt anlegen“ an.</p>}
          {suggestions.isError && <p className="muted small">Die Suche nach passenden Kontakten ist fehlgeschlagen.</p>}
        </>
      )}
    </div>
  );
}
